/**
 * Home - public landing page ("Data Product" v3).
 * Light, clean, product-led: headline + live-prices panel hero, movers +
 * Nigeria coverage map, commodities grid, global futures + navy FX card,
 * ground-truth coverage band, data sources & methodology, CTA. The live
 * ticker sits above the header (PublicLayout). Each section links to its
 * dedicated page. All data comes from the same public hooks as before.
 */

import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { Icon } from '@iconify/react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { AreaChart, Area, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { fadeUp, MotionInView } from '../../utils/animations';
import { useThemeMode, numericSx, radius, MONO, displaySx } from '../../context/ThemeContext';
import SectionHeading from '../../components/public/SectionHeading';
import LivePricesPanel from '../../components/public/LivePricesPanel';
import DeltaChip from '../../components/public/DeltaChip';
import { CountUp } from '../../hooks/useCounter';
import {
  useCommodities, useCategories, useMarkets, useStates, useFxSeries,
  useGlobalCommodities, useGlobalPrices, useDashboardMetrics, usePriceMovements, useLocalPrices,
} from '../../services/queries';
import { useWorldSeriesCatalogue, useWorldReferenceBatch } from '../../services/queries';
import {
  WORLD_MONTHS, WORLD_CARD_SERIES, toPriceMap, toPickerOptions,
  exchangeFeedIsEmpty, attributionOf, cardLabel, monthLabel, unitLabelFor,
  kgPerPublishedUnit,
} from '../../utils/worldReference';
import { isCommodityActive, activeCommodities } from '../../utils/catalogStatus';
import { LoadingSkeleton, WorldAttribution } from '../../components/common';
import { iconForCommodity } from '../../utils/commodityIcons';

const GEO_URL = '/data/nigeria-states.json';
const PILOT = ['Kaduna', 'Taraba', 'Katsina', 'Bauchi'];
const CENTERS = { Kaduna: [7.44, 10.52], Taraba: [10.77, 7.87], Katsina: [7.6, 13.0], Bauchi: [9.84, 10.31] };
const FX_RANGES = [{ l: '1M', w: 4 }, { l: '3M', w: 13 }, { l: '6M', w: 26 }, { l: '1Y', w: 52 }];

const ArrowRight = ({ size = 15, color }) => <Icon icon="ph:arrow-right" width={size} height={size} color={color} />;

// Format ISO date (YYYY-MM-DD) to "18 Apr 2026"
const fmtDate = (d) => {
  if (!d) return '-';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '-';
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

// Latest date across all local price records for a given commodityId
const latestLocalDate = (localPrices, commodityId) => {
  let max = null;
  for (const p of localPrices) {
    if (commodityId && p.commodityId !== commodityId) continue;
    if (!max || p.date > max) max = p.date;
  }
  return max;
};

// The midpoint of a commodity's published bag range, or null when it has
// none. CommodityManagement writes priceRange as {min: null, max: null} for a
// commodity with no bag prices, and (null + null) / 2 is 0, so the cards used
// to print a confident ₦0 for exactly the commodities we know least about.
const midRange = (c) => {
  const { min, max } = c.priceRange || {};
  if (min == null || max == null) return null;
  return Math.round((min + max) / 2);
};

// Week-over-week % change for a local commodity (average across markets)
// Returns { pct, latestPrice } or null if not enough data
const weekOverWeekChange = (localPrices, commodityId) => {
  const byWeek = {};
  for (const p of localPrices) {
    if (p.commodityId !== commodityId) continue;
    // A row with no price is not an observation of zero. Counting it dragged
    // the average down by however many markets had not reported yet.
    if (p.price == null) continue;
    if (!byWeek[p.date]) byWeek[p.date] = { total: 0, count: 0 };
    byWeek[p.date].total += p.price;
    byWeek[p.date].count += 1;
  }
  const weeks = Object.keys(byWeek).sort();
  if (weeks.length < 2) return null;
  const latestAvg = byWeek[weeks[weeks.length - 1]].total / byWeek[weeks[weeks.length - 1]].count;
  const prevAvg = byWeek[weeks[weeks.length - 2]].total / byWeek[weeks[weeks.length - 2]].count;
  if (!prevAvg) return null;
  return { pct: ((latestAvg - prevAvg) / prevAvg) * 100, latestPrice: Math.round(latestAvg) };
};

// Weekly average price series for a local commodity - feeds the hero panel's
// mini chart when a commodity row is selected. Same aggregation as
// weekOverWeekChange, so the plotted line always matches the row's figures.
const weeklySeries = (localPrices, commodityId) => {
  const byWeek = {};
  for (const p of localPrices) {
    if (p.commodityId !== commodityId) continue;
    if (p.price == null) continue;  // same reason as weekOverWeekChange
    if (!byWeek[p.date]) byWeek[p.date] = { total: 0, count: 0 };
    byWeek[p.date].total += p.price;
    byWeek[p.date].count += 1;
  }
  return Object.keys(byWeek)
    .sort()
    .map((w) => ({ date: w, value: Math.round(byWeek[w].total / byWeek[w].count) }));
};

function HomeInner({ commodities, commodityCategories, fxRates, globalCommodities, globalCommodityPrices, dashboardMetrics, priceMovements, localPrices }) {
  const { palette } = useThemeMode();
  const [hoveredState, setHoveredState] = useState(null);
  const [fxW, setFxW] = useState(26);

  // Live showcases (hero panel + commodity strip) exclude RETIRED items.
  const topCom = activeCommodities(commodities, commodityCategories).slice(0, 6);
  // Only rates that carry a figure. `rate` is nullable, and every use below
  // either formats it (toLocaleString throws on null), divides by it, or
  // hands the series to a chart that takes Math.min/max over it. Filtering
  // once here covers all of them, the series passed to LivePricesPanel
  // included.
  const pricedFx = fxRates.filter((r) => r.rate != null);
  const latestFx = pricedFx[pricedFx.length - 1];
  const prevFx = pricedFx[pricedFx.length - 2];
  const fxPct = latestFx && prevFx && prevFx.rate ? ((latestFx.rate - prevFx.rate) / prevFx.rate) * 100 : null;
  const platformD = dashboardMetrics.lastUpdateDate;

  // "Updated · date" label style shared across cards/charts
  const tsSx = { fontSize: '0.64rem', color: palette.textMuted, fontStyle: 'italic', mt: 0.6, letterSpacing: '0.02em' };

  // ── Hero panel data ──
  const heroRows = topCom.slice(0, 3).map((c) => {
    const wow = weekOverWeekChange(localPrices, c.id);
    const avg = wow?.latestPrice ?? midRange(c);
    const bag = c.bagSizes[c.bagSizes.length - 1];
    return {
      name: c.name,
      sub: `per ${bag}kg bag`,
      value: avg == null ? '-' : `₦${avg.toLocaleString()}`,
      change: wow?.pct ?? 0,
      series: weeklySeries(localPrices, c.id).slice(-12),
    };
  });
  const firstGlobal = globalCommodities[0];
  // Priced rows only, so the close below is a number and not a crash.
  const firstGlobalSeries = firstGlobal
    ? (globalCommodityPrices[firstGlobal.id] || []).filter((r) => r.close != null)
    : [];
  const firstGlobalLatest = firstGlobalSeries[firstGlobalSeries.length - 1];
  const heroFootNote = firstGlobal && firstGlobalLatest
    ? `${firstGlobal.name} (${firstGlobal.exchange}) $${firstGlobalLatest.close.toLocaleString()} · ICE & CBOT tracked daily`
    : 'ICE & CBOT settlement feeds tracked daily';

  // ── Global futures cards ──
  // The exchange feed expired, so these four cards read "Awaiting exchange
  // feed" on the home page, which is the first thing a visitor sees, for a
  // feed that is never arriving. Same fallback as the commodity pages.
  const usingWorldReference = exchangeFeedIsEmpty(globalCommodityPrices);
  const { data: worldCatalogue } = useWorldSeriesCatalogue({ enabled: usingWorldReference });
  const worldOptions = useMemo(() => toPickerOptions(worldCatalogue), [worldCatalogue]);
  const worldIds = useMemo(() => worldOptions.map((o) => o.id), [worldOptions]);
  const { data: worldBatch } = useWorldReferenceBatch(
    worldIds, WORLD_MONTHS, { enabled: usingWorldReference && worldIds.length > 0 },
  );
  const worldPrices = useMemo(() => toPriceMap(worldBatch), [worldBatch]);
  const worldAttribution = attributionOf(worldBatch);

  // Four cards, the crops this platform is actually about. Prices are shown
  // per tonne, the familiar magnitude, rather than the per-kilo figures the
  // Pink Sheet publishes for cocoa and coffee.
  const worldFutures = useMemo(() => {
    const byId = Object.fromEntries(worldOptions.map((o) => [o.id, o]));
    return WORLD_CARD_SERIES.filter((id) => byId[id]).slice(0, 4).map((id) => {
      const o = byId[id];
      const { name, detail } = cardLabel(o.label, o.group);
      const kg = kgPerPublishedUnit(o.unit);
      const rows = (worldPrices[id] || []).filter((r) => r.close != null);
      const toTonne = (v) => (kg ? (v / kg) * 1000 : v);
      const latest = rows[rows.length - 1];
      const prev = rows[rows.length - 2];
      const pct = latest && prev && prev.close
        ? ((latest.close - prev.close) / prev.close) * 100
        : null;
      return {
        g: { id, name, exchange: detail, unit: unitLabelFor(o.unit, 'USD', 'MT') },
        latest: latest ? { ...latest, close: toTonne(latest.close) } : undefined,
        pct,
        spark: rows.slice(-16).map((r) => ({ ...r, close: toTonne(r.close) })),
        month: latest ? monthLabel(latest.date) : null,
      };
    });
  }, [worldOptions, worldPrices]);

  const exchangeFutures = globalCommodities.slice(0, 4).map((g) => {
    // Priced rows only. The guard below checked prev.close but never
    // latest.close, so a trailing row with no figure both threw in the price
    // line and rendered a -100% chip.
    const s = (globalCommodityPrices[g.id] || []).filter((r) => r.close != null);
    const latest = s[s.length - 1];
    const prev = s[s.length - 2];
    const pct = latest && prev && prev.close ? ((latest.close - prev.close) / prev.close) * 100 : null;
    return { g, latest, pct, spark: s.slice(-16), month: null };
  });
  const futures = usingWorldReference ? worldFutures : exchangeFutures;

  // ── FX card sparkline ──
  const fxSpark = pricedFx.slice(-fxW).map((r) => ({ date: r.date, rate: r.rate }));

  // Top movers, enriched with the commodity record + latest ₦/100kg at that market
  // (topCommodity is a name string; resolve it against the catalog).
  const movers = useMemo(() => {
    const byName = new Map(commodities.map((c) => [c.name, c]));
    return [...priceMovements]
      .filter((m) => {
        const c = byName.get(m.topCommodity);
        return !c || isCommodityActive(c, commodityCategories);
      })
      .sort((a, b) => Math.abs(b.priceChange) - Math.abs(a.priceChange))
      .slice(0, 6)
      .map((m) => {
        const commodity = byName.get(m.topCommodity) || null;
        let per100 = null;
        if (commodity) {
          let latest = null;
          for (const p of localPrices) {
            if (p.marketId !== m.marketId || p.commodityId !== commodity.id) continue;
            if (!latest || p.date > latest.date) latest = p;
          }
          if (latest) per100 = Math.round((latest.pricePerKg || (latest.price / (latest.bagSize || 100))) * 100);
        }
        return { ...m, commodity, per100 };
      });
  }, [priceMovements, commodities, commodityCategories, localPrices]);

  const tt = { contentStyle: { backgroundColor: palette.tooltipBg, border: `1px solid ${palette.tooltipBorder}`, borderRadius: 10, fontSize: '0.8rem', padding: '10px 14px', color: palette.tooltipText }, labelStyle: { fontWeight: 600, color: palette.accentLight, marginBottom: 4 }, itemStyle: { color: 'rgba(255,255,255,0.75)', fontSize: '0.78rem' } };

  const cardSx = {
    bgcolor: palette.card,
    border: `1px solid ${palette.cardBorder}`,
    borderRadius: `${radius.card}px`,
    boxShadow: palette.shadowSm,
  };

  return (
    <Box sx={{ bgcolor: palette.bg }}>

      {/* ===== HERO - headline + live-prices product panel ===== */}
      {/* (The live ticker sits at the very top of the page, above the header.) */}
      <Box component="section" sx={{ position: 'relative', overflow: 'visible', borderBottom: `1px solid ${palette.divider}` }}>
        <Box aria-hidden sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(720px 360px at 88% 4%, rgba(34,197,94,0.06), transparent 70%)' }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, pt: { xs: 6, md: 9 }, pb: { xs: 8, md: 11 } }}>
          {/* Concept proportions: the product panel is the wider column (1.18fr vs 1.02fr) */}
          <Grid container spacing={{ xs: 6, md: 8 }} alignItems="center">
            {/* Left: statement */}
            <Grid item xs={12} md={5.6}>
              <MotionInView variants={fadeUp}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.65, mb: 3, bgcolor: palette.surface, border: `1px solid ${palette.divider}`, borderRadius: 999 }}>
                  <Icon icon="ph:map-pin" width={15} height={15} color={palette.accent} />
                  <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 500, color: palette.textSecondary }}>
                    Commodity intelligence · <Box component="b" sx={{ color: palette.text, fontWeight: 700 }}>Northern Nigeria pilot</Box>
                  </Typography>
                </Box>

                <Typography component="h1" sx={{ ...displaySx, color: palette.text }}>
                  Nigeria's commodity markets, <Box component="span" sx={{ color: palette.accent }}>decoded.</Box>
                </Typography>

                <Typography sx={{ mt: 2.5, color: palette.textSecondary, fontSize: { xs: '1.02rem', md: '1.12rem' }, lineHeight: 1.6, maxWidth: 490 }}>
                  Transparent, trustworthy prices in one place - local market rates collected weekly by field agents, global futures benchmarks, and the NGN/USD close.
                </Typography>

                <Box sx={{ mt: 4, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
                  <Button component={Link} to="/commodities" variant="contained" disableElevation endIcon={<ArrowRight color="#fff" />}
                    sx={{ bgcolor: palette.accent, color: '#fff', fontWeight: 600, px: 3, py: 1.35, fontSize: '0.95rem', borderRadius: '10px', '&:hover': { bgcolor: '#1C9550', boxShadow: palette.shadowMd } }}>
                    Explore live prices
                  </Button>
                  <Button component={Link} to="/insights" endIcon={<ArrowRight color={palette.text} />}
                    sx={{ color: palette.text, fontWeight: 600, fontSize: '0.92rem', px: 1, '&:hover': { bgcolor: 'transparent', color: palette.accent } }}>
                    Currency analysis
                  </Button>
                </Box>

                {/* Trust stats */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3.5, borderTop: `1px solid ${palette.divider}`, pt: 3, mt: 4.5, maxWidth: 500 }}>
                  {[
                    { v: dashboardMetrics.activeMarkets || 0, l: 'Active markets' },
                    { v: dashboardMetrics.totalCommodities || 0, l: 'Commodities tracked' },
                    { v: localPrices.length, l: 'Price records' },
                  ].map((s, i) => (
                    <React.Fragment key={s.l}>
                      {i > 0 && <Box sx={{ width: '1px', height: 34, bgcolor: palette.divider }} />}
                      <Box>
                        <CountUp value={s.v} sx={{ ...numericSx, display: 'block', fontSize: '1.3rem', fontWeight: 600, color: palette.text, lineHeight: 1.2 }} />
                        <Typography sx={{ fontSize: '0.76rem', color: palette.textMuted, fontWeight: 500 }}>{s.l}</Typography>
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </MotionInView>
            </Grid>

            {/* Right: live prices product panel */}
            <Grid item xs={12} md={6.4}>
              <MotionInView variants={fadeUp} custom={0.5}>
                <LivePricesPanel
                  fx={latestFx ? { rate: latestFx.rate, pct: fxPct, date: fmtDate(latestFx.date) } : null}
                  fxSeries={pricedFx.slice(-12).map((r) => ({ date: r.date, rate: r.rate }))}
                  rows={heroRows}
                  footNote={heroFootNote}
                  footRight={`Updated · ${fmtDate(latestFx?.date || platformD)}`}
                />
              </MotionInView>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== MOVERS + COVERAGE MAP ===== */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 }, bgcolor: palette.bg }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Local Market Movers"
            title="This week's price movement"
            subtitle="Collected on the ground by field agents across the Northern pilot corridor."
            actions={<Typography sx={{ ...numericSx, fontSize: '0.74rem', color: palette.textMuted, whiteSpace: 'nowrap' }}>7-day change · field-verified</Typography>}
            sx={{ mb: { xs: 3, md: 4.5 } }}
          />

          <MotionInView variants={fadeUp}>
          <Grid container spacing={3} alignItems="stretch">
            {/* Movers table - commodity-led, with the ₦/100kg price (market folded into the sub-line) */}
            <Grid item xs={12} md={7} sx={{ display: 'flex' }}>
                <Box sx={{ ...cardSx, overflow: 'hidden', width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr auto', gap: 2, px: 3, py: 1.4, bgcolor: palette.surface, borderBottom: `1px solid ${palette.divider}` }}>
                    {['Commodity', '₦ / 100kg', '7-day'].map((h, i) => (
                      <Typography key={h} sx={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: palette.textMuted, textAlign: i === 0 ? 'left' : 'right' }}>{h}</Typography>
                    ))}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {movers.map((m, i) => (
                      <Box key={m.marketId} sx={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr auto', gap: 2, alignItems: 'center', px: 3, py: 1.55, borderBottom: i < movers.length - 1 ? `1px solid ${palette.divider}` : 'none', transition: 'background-color 0.15s', '&:hover': { bgcolor: palette.cardHover } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                          <Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Icon icon={iconForCommodity(m.commodity)} width={17} height={17} color={palette.navy} />
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontWeight: 600, fontSize: '0.88rem', color: palette.text, letterSpacing: '-0.01em' }}>{m.topCommodity}</Typography>
                            <Typography noWrap sx={{ fontSize: '0.72rem', color: palette.textMuted }}>{m.marketName}, {m.state}</Typography>
                          </Box>
                        </Box>
                        <Typography sx={{ ...numericSx, fontSize: '0.9rem', fontWeight: 500, color: m.per100 != null ? palette.text : palette.textMuted, textAlign: 'right' }}>
                          {m.per100 != null ? `₦${m.per100.toLocaleString()}` : '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <DeltaChip value={m.priceChange} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 1.5, bgcolor: palette.surface, borderTop: `1px solid ${palette.divider}` }}>
                    <Typography sx={{ fontSize: '0.76rem', color: palette.textSecondary }}>Top 6 moves across {dashboardMetrics.activeMarkets || 24} markets</Typography>
                    <Button component={Link} to="/markets" endIcon={<ArrowRight color={palette.accent} size={14} />} sx={{ color: palette.accent, fontWeight: 600, fontSize: '0.8rem', p: 0, '&:hover': { bgcolor: 'transparent' } }}>
                      All markets
                    </Button>
                  </Box>
                </Box>
            </Grid>

            {/* Nigeria coverage map - stretches to match the table height */}
            <Grid item xs={12} md={5} sx={{ display: 'flex' }}>
                <Box sx={{ ...cardSx, p: 2.5, width: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: palette.text }}>Market coverage</Typography>
                    <Typography sx={{ ...numericSx, fontSize: '0.68rem', color: palette.textMuted }}>4 pilot states</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ComposableMap projection="geoMercator" projectionConfig={{ center: [8, 9.5], scale: 2800 }} style={{ width: '100%', height: '100%', maxHeight: 420 }}>
                    <Geographies geography={GEO_URL}>
                      {({ geographies }) => geographies.map((geo) => {
                        const name = geo.properties.NAME_1 || geo.properties.name;
                        const pilot = PILOT.includes(name);
                        const hov = hoveredState === name;
                        return (
                          <Geography key={geo.rsmKey} geography={geo}
                            fill={pilot ? (hov ? palette.accentStrong : palette.accent) : (hov ? '#E2E7EC' : palette.surfaceHover)}
                            stroke="#fff" strokeWidth={0.8}
                            onMouseEnter={() => setHoveredState(name)} onMouseLeave={() => setHoveredState(null)}
                            style={{ default: { outline: 'none' }, hover: { outline: 'none', cursor: 'pointer' }, pressed: { outline: 'none' } }}
                          />
                        );
                      })}
                    </Geographies>
                    {Object.entries(CENTERS).map(([n, c]) => (
                      <Marker key={n} coordinates={c}>
                        <circle r={5} fill="#fff" stroke={palette.accent} strokeWidth={2} />
                        <circle r={10} fill="none" stroke={palette.accent} strokeWidth={1} opacity={0.4}><animate attributeName="r" from="5" to="15" dur="2s" repeatCount="indefinite" /><animate attributeName="opacity" from="0.4" to="0" dur="2s" repeatCount="indefinite" /></circle>
                        <text textAnchor="middle" y={-16} style={{ fontSize: '9px', fontWeight: 700, fill: palette.navy, fontFamily: 'inherit' }}>{n}</text>
                      </Marker>
                    ))}
                  </ComposableMap>
                  </Box>
                  <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', minHeight: 22 }}>
                    {hoveredState ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: PILOT.includes(hoveredState) ? palette.accent : palette.textMuted }} />
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: palette.text }}>{hoveredState} {PILOT.includes(hoveredState) ? '- Pilot state' : '- Coming soon'}</Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: palette.accent }} />
                          <Typography sx={{ fontSize: '0.72rem', color: palette.textSecondary }}>Pilot state</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: palette.surfaceHover, border: `1px solid ${palette.cardBorder}` }} />
                          <Typography sx={{ fontSize: '0.72rem', color: palette.textSecondary }}>Coming soon</Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
            </Grid>
          </Grid>
          </MotionInView>
        </Container>
      </Box>

      {/* ===== COMMODITIES GRID ===== */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 }, bgcolor: palette.surface, borderTop: `1px solid ${palette.divider}`, borderBottom: `1px solid ${palette.divider}` }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Commodities"
            title="Track what matters most"
            subtitle="Focal commodities across the pilot corridor, priced weekly per bag."
            actions={<Button component={Link} to="/commodities" endIcon={<ArrowRight color={palette.accent} size={14} />} sx={{ color: palette.accent, fontWeight: 600, fontSize: '0.84rem', '&:hover': { bgcolor: 'transparent' } }}>View all</Button>}
            sx={{ mb: { xs: 3, md: 4.5 } }}
          />
          <Grid container spacing={2}>
            {topCom.map((c, i) => {
              const wow = weekOverWeekChange(localPrices, c.id);
              const avg = wow?.latestPrice ?? midRange(c);
              const ch = wow ? wow.pct : 0;
              const bagSize = c.bagSizes[c.bagSizes.length - 1];
              const updated = latestLocalDate(localPrices, c.id) || platformD;
              return (
                <Grid item xs={6} sm={4} md={2} key={c.id}>
                  <MotionInView variants={fadeUp} custom={i * 0.15}>
                    <Box sx={{ ...cardSx, p: 2, display: 'flex', flexDirection: 'column', minHeight: 170, transition: 'border-color 0.2s, box-shadow 0.2s', '&:hover': { borderColor: palette.textMuted, boxShadow: palette.shadowMd } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon icon={iconForCommodity(c)} width={19} height={19} color={palette.navy} />
                        </Box>
                        <DeltaChip value={ch} size="sm" />
                      </Box>
                      <Box sx={{ mt: 'auto' }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: palette.text, mb: 0.3, lineHeight: 1.25 }}>{c.name}</Typography>
                        <Typography sx={{ ...numericSx, fontWeight: 600, fontSize: '1rem', color: avg == null ? palette.textMuted : palette.text }}>{avg == null ? '-' : `₦${avg.toLocaleString()}`}</Typography>
                        <Typography sx={{ fontSize: '0.66rem', color: palette.textMuted, mt: 0.1 }}>per {bagSize}kg bag</Typography>
                        <Typography sx={tsSx}>Updated · {fmtDate(updated)}</Typography>
                      </Box>
                    </Box>
                  </MotionInView>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* ===== GLOBAL FUTURES + NGN/USD ===== */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 }, bgcolor: palette.bg }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="World Prices & Currency"
            title="The world price, beside the naira"
            subtitle={usingWorldReference
              ? 'Monthly world averages from the World Bank Pink Sheet, beside the weekly NGN/USD close, so a Nigerian number always has global context.'
              : 'Futures from ICE and CBOT, aligned to the weekly NGN/USD close so a Nigerian number always has global context.'}
            actions={<Button component={Link} to="/insights" endIcon={<ArrowRight color={palette.accent} size={14} />} sx={{ color: palette.accent, fontWeight: 600, fontSize: '0.84rem', '&:hover': { bgcolor: 'transparent' } }}>Currency analysis</Button>}
            sx={{ mb: { xs: 3, md: 4.5 } }}
          />

          <Grid container spacing={3}>
            {/* Futures 2×2 */}
            <Grid item xs={12} md={7}>
              <MotionInView variants={fadeUp}>
                <Grid container spacing={2}>
                  {futures.map(({ g, latest, pct, spark, month }) => (
                    <Grid item xs={12} sm={6} key={g.id}>
                      <Box sx={{ ...cardSx, p: 2.5, transition: 'border-color 0.2s, box-shadow 0.2s', '&:hover': { borderColor: palette.textMuted, boxShadow: palette.shadowMd } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
                            <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: palette.surface, border: `1px solid ${palette.cardBorder}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <Icon icon="ph:globe-simple" width={15} height={15} color={palette.navy} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: palette.text }}>{g.name}</Typography>
                              <Typography sx={{ fontSize: '0.68rem', color: palette.textMuted, fontWeight: 500 }}>{g.exchange}{g.unit ? ` · ${g.unit}` : ''}</Typography>
                            </Box>
                          </Box>
                          {pct != null && <DeltaChip value={pct} size="sm" />}
                        </Box>
                        {latest ? (
                          <>
                            <Typography sx={{ ...numericSx, fontSize: '1.3rem', fontWeight: 600, color: palette.text }}>
                              ${latest.close.toLocaleString()}
                            </Typography>
                            <Box sx={{ height: 34, mt: 1 }}>
                              <ResponsiveContainer>
                                <AreaChart data={spark} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                                  <defs>
                                    <linearGradient id={`fut-${g.id}`} x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="0%" stopColor={pct != null && pct < 0 ? palette.danger : palette.accent} stopOpacity={0.16} />
                                      <stop offset="100%" stopColor={pct != null && pct < 0 ? palette.danger : palette.accent} stopOpacity={0} />
                                    </linearGradient>
                                  </defs>
                                  <YAxis hide domain={['dataMin', 'dataMax']} />
                                  <Area type="monotone" dataKey="close" stroke={pct != null && pct < 0 ? palette.danger : palette.accent} strokeWidth={1.6} fill={`url(#fut-${g.id})`} dot={false} />
                                </AreaChart>
                              </ResponsiveContainer>
                            </Box>
                            {month && (
                              <Typography sx={{ fontSize: '0.64rem', color: palette.textMuted, fontStyle: 'italic', mt: 0.6 }}>
                                {month}
                              </Typography>
                            )}
                          </>
                        ) : (
                          <Typography sx={{ fontSize: '0.78rem', color: palette.textMuted, py: 2 }}>
                            {usingWorldReference ? 'Awaiting world reference' : 'Awaiting exchange feed'}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                {/* CC BY 4.0 obliges the credit wherever the data appears,
                    and these four cards are the first figures on the site. */}
                {usingWorldReference && (
                  <WorldAttribution text={worldAttribution} sx={{ mt: 1.5 }} />
                )}
              </MotionInView>
            </Grid>

            {/* Navy FX anchor card */}
            <Grid item xs={12} md={5}>
              <MotionInView variants={fadeUp} custom={1}>
                <Box sx={{ position: 'relative', overflow: 'hidden', bgcolor: palette.navy, color: '#fff', borderRadius: `${radius.card}px`, boxShadow: palette.shadowMd, p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box aria-hidden sx={{ position: 'absolute', right: -60, top: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(closest-side, rgba(34,197,94,0.16), transparent)' }} />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.72)' }}>NGN / USD</Typography>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {FX_RANGES.map((r) => (
                        <Button key={r.l} size="small" onClick={() => setFxW(r.w)}
                          sx={{ minWidth: 34, px: 0.8, py: 0.2, fontFamily: MONO, fontSize: '0.66rem', fontWeight: 600, borderRadius: '6px', color: fxW === r.w ? palette.navy : 'rgba(255,255,255,0.65)', bgcolor: fxW === r.w ? palette.accentLight : 'rgba(255,255,255,0.08)', '&:hover': { bgcolor: fxW === r.w ? palette.accentLight : 'rgba(255,255,255,0.16)' } }}>
                          {r.l}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {latestFx ? (
                    <>
                      <Typography sx={{ ...numericSx, fontSize: '2.5rem', fontWeight: 600, letterSpacing: '-0.03em', mt: 2, position: 'relative', zIndex: 1 }}>
                        ₦{latestFx.rate.toLocaleString()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, mt: 0.5, position: 'relative', zIndex: 1 }}>
                        {fxPct != null && (
                          <Box component="span" sx={{ ...numericSx, display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 0.9, py: 0.3, borderRadius: '6px', fontSize: '0.76rem', color: '#F4C77B', bgcolor: 'rgba(180,83,9,0.28)' }}>
                            {fxPct >= 0 ? '▲' : '▼'} {fxPct >= 0 ? '+' : '−'}{Math.abs(fxPct).toFixed(1)}% this week
                          </Box>
                        )}
                        <Typography sx={{ fontSize: '0.76rem', color: 'rgba(255,255,255,0.6)' }}>Weekly close · CBN</Typography>
                      </Box>
                      <Box sx={{ mt: 'auto', pt: 2.5, position: 'relative', zIndex: 1 }}>
                        <Box sx={{ height: 76 }}>
                          <ResponsiveContainer>
                            <AreaChart data={fxSpark} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                              <defs>
                                <linearGradient id="fxNavy" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="0%" stopColor={palette.accentLight} stopOpacity={0.22} />
                                  <stop offset="100%" stopColor={palette.accentLight} stopOpacity={0} />
                                </linearGradient>
                              </defs>
                              <YAxis hide domain={['dataMin - 20', 'dataMax + 20']} />
                              <RechartsTooltip {...tt} formatter={(v) => [`₦${v.toLocaleString()}`, 'NGN/USD']} labelFormatter={(l, p) => p?.[0]?.payload?.date || ''} />
                              <Area type="monotone" dataKey="rate" stroke={palette.accentLight} strokeWidth={2} fill="url(#fxNavy)" dot={false} />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.1)', position: 'relative', zIndex: 1 }}>
                        <Icon icon="ph:warning" width={14} height={14} color="#F4C77B" style={{ flexShrink: 0, marginTop: 2 }} />
                        <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.62)', lineHeight: 1.5 }}>
                          A rising rate means a weaker naira - read as caution, it lifts the cost of imported inputs.
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ flex: 1, mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', borderRadius: '10px', border: '1px dashed rgba(255,255,255,0.2)', position: 'relative', zIndex: 1, p: 3 }}>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#fff' }}>No FX data yet</Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.6)', mt: 0.5, maxWidth: 260 }}>NGN/USD rates appear here once the exchange-rate feed is published.</Typography>
                    </Box>
                  )}
                </Box>
              </MotionInView>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== GROUND TRUTH / COVERAGE BAND ===== */}
      <Box component="section" sx={{ py: { xs: 8, md: 11 }, bgcolor: palette.surface, borderTop: `1px solid ${palette.divider}`, borderBottom: `1px solid ${palette.divider}` }}>
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <MotionInView variants={fadeUp}>
                <SectionHeading
                  eyebrow="Coverage"
                  title="Ground truth, from four pilot states"
                  subtitle="Field agents log verified prices across the Northern corridor every week - the foundation of every number on Farmgate."
                  animate={false}
                  sx={{ mb: 3.5 }}
                />
                {/* One joined stat card - 2×2 grid with internal hairline dividers */}
                <Box sx={{ bgcolor: '#fff', border: `1px solid ${palette.cardBorder}`, borderRadius: '12px', boxShadow: palette.shadowSm, overflow: 'hidden', display: 'grid', gridTemplateColumns: '1fr 1fr', maxWidth: 520 }}>
                  {[
                    { v: dashboardMetrics.activeMarkets || 0, l: 'Active markets' },
                    { v: dashboardMetrics.totalCommodities || 0, l: 'Commodities tracked' },
                    { v: globalCommodities.length, l: 'Global futures' },
                    { v: localPrices.length, l: 'Price records logged' },
                  ].map((s, i) => (
                    <Box key={s.l} sx={{ px: 3, py: 2.5, borderRight: i % 2 === 0 ? `1px solid ${palette.divider}` : 'none', borderBottom: i < 2 ? `1px solid ${palette.divider}` : 'none' }}>
                      <CountUp value={s.v} sx={{ ...numericSx, display: 'block', fontSize: '1.9rem', fontWeight: 600, color: palette.text, lineHeight: 1.15, letterSpacing: '-0.02em' }} />
                      <Typography sx={{ fontSize: '0.82rem', color: palette.textSecondary, fontWeight: 500, mt: 0.4 }}>{s.l}</Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, mt: 3 }}>
                  {PILOT.map((s) => (
                    <Box key={s} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, px: 1.6, py: 0.7, borderRadius: 999, bgcolor: '#fff', border: `1px solid ${palette.cardBorder}` }}>
                      <Icon icon="ph:map-pin" width={14} height={14} color={palette.accent} />
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: palette.text }}>{s}</Typography>
                    </Box>
                  ))}
                </Box>
                <Button component={Link} to="/markets" endIcon={<ArrowRight color={palette.accent} size={14} />} sx={{ mt: 2.5, color: palette.accent, fontWeight: 600, fontSize: '0.84rem', p: 0, '&:hover': { bgcolor: 'transparent' } }}>
                  Explore markets
                </Button>
              </MotionInView>
            </Grid>
            <Grid item xs={12} md={6}>
              <MotionInView variants={fadeUp} custom={1}>
                <Box sx={{ position: 'relative', borderRadius: `${radius.card}px`, overflow: 'hidden', boxShadow: palette.shadowMd }}>
                  <Box component="img" src="/images/hero/hero-2.jpg" alt="A Nigerian farmer holding freshly harvested maize" sx={{ display: 'block', width: '100%', height: { xs: 300, md: 440 }, objectFit: 'cover' }} />
                  <Box sx={{ position: 'absolute', left: 18, right: 18, bottom: 18, bgcolor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(6px)', borderRadius: '12px', px: 2.25, py: 1.6, display: 'flex', alignItems: 'center', gap: 1.4 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: '9px', flexShrink: 0, bgcolor: palette.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon icon="ph:shield-check" width={19} height={19} color={palette.accent} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: palette.text }}>Verified on the ground</Typography>
                      <Typography sx={{ fontSize: '0.74rem', color: palette.textSecondary }}>Every price traced to a named market & date</Typography>
                    </Box>
                  </Box>
                </Box>
              </MotionInView>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ===== DATA SOURCES & METHODOLOGY ===== */}
      <Box component="section" sx={{ py: { xs: 8, md: 10 }, bgcolor: palette.bg }}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="Data Sources & Methodology"
            title="Where does this data come from?"
            subtitle="Farmgate aggregates commodity data from field agents and global exchanges. Every data point is traceable to its origin."
            sx={{ mb: { xs: 3, md: 4.5 } }}
          />

          <Grid container spacing={2}>
            {[
              {
                chip: 'LOCAL PRICES',
                title: 'Nigerian Market Prices',
                partner: 'Field Agents',
                methodology: 'Weekly field collection from local markets across Nigeria. Prices are captured by Field Agents in local currency and averaged per market.',
                cadence: 'Weekly · ~25% fill rate',
              },
              {
                chip: 'GLOBAL FUTURES',
                title: 'International Commodity Exchanges',
                partner: 'ICE · CBOT',
                methodology: 'Live settlement prices for Cocoa, Soybeans, Soymeal, Coffee and Sugar sourced directly from the Intercontinental Exchange (ICE) and Chicago Board of Trade (CBOT) via exchange data feeds.',
                cadence: 'Daily close (OHLCV)',
              },
              {
                chip: 'EXCHANGE RATE',
                title: 'NGN/USD Conversion',
                partner: 'Central Bank of Nigeria (CBN)',
                methodology: 'Official NGN/USD exchange rate published by the CBN. Used to compute FX-adjusted commodity prices and dollar-denominated comparisons across charts.',
                cadence: 'Weekly close',
              },
              {
                chip: 'GOVERNANCE',
                title: 'Quality & Attribution',
                partner: 'Triple Gee Merchants Ltd',
                methodology: 'Field agent submissions are spot-audited by Triple Gee Merchants Ltd. Anomalies (>20% week-on-week) trigger review. Data provenance is retained per record.',
                cadence: 'Continuous · Monthly review',
              },
            ].map((src) => (
              <Grid item xs={12} sm={6} md={3} key={src.chip}>
                <Box sx={{ ...cardSx, p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'inline-block', mb: 1.5 }}>
                    <Typography sx={{ display: 'inline-block', fontFamily: MONO, fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.08em', color: palette.accent, bgcolor: palette.accentBg, px: 0.9, py: 0.35, borderRadius: '5px' }}>
                      {src.chip}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: palette.text, mb: 0.5, letterSpacing: '-0.01em' }}>
                    {src.title}
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: palette.accent, fontWeight: 600, mb: 1.2 }}>
                    {src.partner}
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: palette.textSecondary, lineHeight: 1.55, mb: 1.5 }}>
                    {src.methodology}
                  </Typography>
                  <Typography sx={{ ...numericSx, fontSize: '0.68rem', color: palette.textMuted, mt: 'auto', pt: 1.2, borderTop: `1px solid ${palette.divider}` }}>
                    {src.cadence}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography sx={{ fontSize: '0.76rem', color: palette.textMuted, mt: 3, lineHeight: 1.6 }}>
            All figures shown on Farmgate are aggregated for transparency. Individual market observations are available to verified partners after sign-in. Contact <Box component="span" sx={{ color: palette.accent, fontWeight: 600 }}>data@farmgate.ng</Box> to request the methodology document or raw-data access.
          </Typography>
        </Container>
      </Box>

      {/* ===== CTA ===== */}
      <Box component="section" sx={{ py: { xs: 9, md: 12 }, bgcolor: palette.bg }}>
        <Container maxWidth="lg">
          <MotionInView variants={fadeUp}>
            <Box sx={{ ...cardSx, position: 'relative', overflow: 'hidden', borderRadius: `${radius.panel}px`, p: { xs: 4.5, md: 7 }, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, gap: 4 }}>
              {/* Whisper of green in the card's corner */}
              <Box aria-hidden sx={{ position: 'absolute', right: -80, bottom: -120, width: 520, height: 360, pointerEvents: 'none', background: 'radial-gradient(closest-side, rgba(34,197,94,0.09), transparent)' }} />
              <Box sx={{ position: 'relative', zIndex: 1 }}>
                <Typography variant="h2" sx={{ color: palette.text, fontSize: { xs: '1.8rem', md: '2.2rem' }, mb: 1.25 }}>Decisions deserve a clearer price.</Typography>
                <Typography sx={{ color: palette.textSecondary, fontSize: '1rem', maxWidth: 470, lineHeight: 1.6 }}>Join traders, processors and institutional partners using Farmgate to read Nigeria's commodity markets with confidence.</Typography>
              </Box>
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.75, flexShrink: 0 }}>
                <Button component={Link} to="/login" variant="contained" disableElevation endIcon={<ArrowRight color="#fff" />}
                  sx={{ bgcolor: palette.accent, color: '#fff', fontWeight: 600, px: 3.25, py: 1.35, fontSize: '0.95rem', borderRadius: '10px', '&:hover': { bgcolor: '#1C9550', boxShadow: palette.shadowMd } }}>
                  Request access
                </Button>
                <Button component="a" href="mailto:data@farmgate.ng"
                  sx={{ color: palette.text, fontWeight: 600, px: 3, py: 1.3, fontSize: '0.95rem', borderRadius: '10px', border: `1px solid ${palette.cardBorder}`, bgcolor: '#fff', '&:hover': { borderColor: palette.textMuted, bgcolor: palette.surface } }}>
                  Talk to the team
                </Button>
              </Box>
            </Box>
          </MotionInView>
        </Container>
      </Box>
    </Box>
  );
}

export default function Home() {
  const c = useCommodities();
  const cat = useCategories();
  const m = useMarkets();
  const ps = useStates();
  const fx = useFxSeries('ALL');
  const gc = useGlobalCommodities();
  const gp = useGlobalPrices('ALL');
  const dm = useDashboardMetrics();
  const pm = usePriceMovements();
  const lp = useLocalPrices();
  const loading = [c, cat, m, ps, fx, gc, gp, dm, pm, lp].some((q) => q.isLoading);
  if (loading) return <LoadingSkeleton variant="page" />;
  return (
    <HomeInner
      commodities={c.data || []}
      commodityCategories={cat.data || []}
      fxRates={fx.data || []}
      globalCommodities={gc.data || []}
      globalCommodityPrices={gp.data || {}}
      dashboardMetrics={dm.data || {}}
      priceMovements={pm.data || []}
      localPrices={lp.data || []}
    />
  );
}
